import { Link, createFileRoute } from '@tanstack/react-router'
import { AlertTriangleIcon, ArrowLeftIcon } from 'lucide-react'
import { formatPrice, type OpsOrderDetail } from '@hedgehog/shared'
import { opsApi } from '@/components/ops/api'
import { OrderActions } from '@/components/ops/order-actions'
import {
  AuditSection,
  EmailsTable,
  JobsSection,
  NotesSection,
  ReviewsSection,
  RunsSection,
  EmDashSection,
  ScreensSection,
  TimelineSection,
} from '@/components/ops/order-sections'
import { Facts, Muted, Section } from '@/components/ops/section'
import { FeaturesSection } from '@/components/ops/features'
import { FixPanel } from '@/components/ops/fix-panel'
import { StateBadge } from '@/components/ops/state-badge'
import { formatUsd } from '@/components/ops/format'
import { CopyButton } from '@/components/app/copy-button'
import { ErrorState, LoadingState, errorStatus } from '@/components/app/states'
import { formatAge, formatDateTime } from '@/components/app/format'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'

export const Route = createFileRoute('/ops/orders/$orderId')({
  loader: ({ params }) => opsApi<OpsOrderDetail>(`/ops/orders/${encodeURIComponent(params.orderId)}`),
  head: ({ loaderData }) => ({ meta: [{ title: `${loaderData?.order.businessName ?? 'Order'} · Ops` }] }),
  component: OrderPage,
  pendingComponent: () => <LoadingState label="Loading order" />,
  errorComponent: ({ error }) => (
    <ErrorState error={error} title={errorStatus(error) === 404 ? 'Order not found' : 'Could not load the order'}>
      <Link to="/ops" className="text-sm underline underline-offset-4">
        Back to orders
      </Link>
    </ErrorState>
  ),
})

function OrderPage() {
  const detail = Route.useLoaderData()
  const { order } = detail

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Link to="/ops" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground" aria-label="Back to orders">
          <ArrowLeftIcon className="size-4" aria-hidden />
        </Link>
        <h1 className="text-base font-semibold">{order.businessName}</h1>
        <a href={order.url} target="_blank" rel="noreferrer noopener" className="text-muted-foreground underline-offset-4 hover:underline">
          {order.domain}
        </a>
        <StateBadge state={order.state} />
        <span className={order.overdue ? 'font-medium text-destructive' : 'text-muted-foreground'}>
          {order.overdue && <AlertTriangleIcon className="mr-1 inline size-3.5 align-[-2px]" aria-hidden />}
          {formatAge(order.stateEnteredAt)} in state{order.overdue && ' · overdue'}
        </span>
        {order.escalate && <Badge variant="outline">escalation model</Badge>}
        {order.claudeOnly && <Badge variant="outline">Claude only</Badge>}
        <span className="ml-auto font-mono text-xs text-muted-foreground">{order.id}</span>
      </div>

      {order.lastError && (
        <Alert variant="destructive">
          <AlertTriangleIcon aria-hidden />
          <AlertTitle>Last error</AlertTitle>
          <AlertDescription className="font-mono text-xs break-words whitespace-pre-wrap">{order.lastError}</AlertDescription>
        </Alert>
      )}

      <div className="grid items-start gap-3 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="flex min-w-0 flex-col gap-3">
          <ScreensSection approval={detail.approval} held={detail.held} footer={<FixPanel detail={detail} />} />
          <ReviewsSection reviews={detail.reviews} />
          <RunsSection runs={detail.runs} budgetUsd={order.budgetUsd} costUsd={order.costUsd} />
          <NotesSection approval={detail.approval} />
          <AuditSection audit={detail.audit} />
          <TimelineSection events={detail.events} />
          <JobsSection jobs={detail.jobs} />
          <Section id="emails" title={`Emails sent (${detail.emails.length})`}>
            {detail.emails.length === 0 ? <Muted>No email sent yet.</Muted> : <EmailsTable emails={detail.emails} />}
          </Section>
        </div>

        <aside className="flex flex-col gap-3 xl:sticky xl:top-14" aria-label="Order actions and facts">
          <Section id="actions" title="Actions">
            <OrderActions detail={detail} />
          </Section>
          <EmDashSection emdash={detail.emdash} />
          <FeaturesSection detail={detail} />
          <Section id="facts" title="Order">
            <Facts
              items={[
                ['Email', order.email ? <a href={`mailto:${order.email}`} className="underline-offset-4 hover:underline">{order.email}</a> : '—'],
                ['Profile', order.profile.replace('_', ' ')],
                ['Created', formatDateTime(order.createdAt)],
                ['State since', formatDateTime(order.stateEnteredAt)],
                ['Timeout', order.timeoutAt ? formatDateTime(order.timeoutAt) : '—'],
                ['AI cost', `${formatUsd(order.costUsd)} of ${formatUsd(order.budgetUsd)} budget`],
                ['Price', formatPrice(order.priceCents, order.currency)],
                [
                  'Renewal',
                  <>
                    {detail.renewal.yearlyCents == null ? 'price not set' : `${formatPrice(detail.renewal.yearlyCents, order.currency)} / year`}
                    {detail.renewal.renewsAt && ` · ${formatDateTime(detail.renewal.renewsAt)}`}
                    {detail.renewal.subscriptionId && <span className="block font-mono text-xs text-muted-foreground">{detail.renewal.subscriptionId}</span>}
                    {detail.renewal.pending && <span className="block text-xs font-medium text-destructive">Pending: {detail.renewal.pending}</span>}
                  </>,
                ],
                ['Review fails', order.reviewFails],
                ['Change round', order.changeRound],
                [
                  'Preview',
                  order.previewToken ? (
                    // Plain anchor: the customer preview route is built separately.
                    <a href={`/preview/${order.previewToken}`} target="_blank" rel="noreferrer" className="underline-offset-4 hover:underline">
                      Open preview page
                    </a>
                  ) : (
                    '—'
                  ),
                ],
              ]}
            />
          </Section>
          <Section id="access" title="Customer access link">
            <div className="flex flex-col gap-2">
              <code className="block rounded bg-muted px-2 py-1 font-mono text-xs break-all">{order.accessLink}</code>
              <div className="flex items-center gap-2">
                <CopyButton value={order.accessLink} label="customer access link" size="xs" />
                <span className="text-xs text-muted-foreground">Signs the holder in to this order. Share only with the customer.</span>
              </div>
            </div>
          </Section>
        </aside>
      </div>
    </div>
  )
}
